import { resendEmailService, type ResendEmailData, type JoinRequestEmailData, type TradeResponseEmailData, type JoinRequestDecisionEmailData, type DraftTurnEmailData, type LeagueInvitationEmailData, type TradeFinalizedEmailData, type AdminNewUserEmailData, type AdminNewLeagueEmailData } from '../services/resendEmailService';
import { supabase } from '../supabase';

// Re-export du type pour compatibilité
export type TradeEmailData = ResendEmailData;

export async function sendTradeNotificationEmail(data: TradeEmailData): Promise<{ success: boolean; error?: string }> {
  try {
    console.log('📧 Sending trade notification email from frontend...');
    const result = await resendEmailService.sendTradeNotification(data);
    
    if (result.success) {
      console.log('✅ Trade notification email sent successfully via Resend from frontend');
    } else {
      console.warn('⚠️ Failed to send trade notification email:', result.error);
    }
    
    return result;
  } catch (error: any) {
    console.error('❌ Error sending trade notification email from frontend:', error);
    return { success: false, error: error.message || 'Unknown error occurred' };
  }
}

// Fonction pour extraire les détails des joueurs d'un trade
export function extractTradeAssets(trades: any[]): {
  senderAssets: Array<{ name: string; type: 'player' | 'goalie' | 'pick'; salary?: number }>;
  receiverAssets: Array<{ name: string; type: 'player' | 'goalie' | 'pick'; salary?: number }>;
} {
  const senderAssets: Array<{ name: string; type: 'player' | 'goalie' | 'pick'; salary?: number }> = [];
  const receiverAssets: Array<{ name: string; type: 'player' | 'goalie' | 'pick'; salary?: number }> = [];

  trades.forEach(trade => {
    // Ajouter les assets du sender
    if (trade.sender_player_name) {
      senderAssets.push({
        name: trade.sender_player_name,
        type: 'player',
        salary: trade.sender_salary
      });
    }
    if (trade.sender_goalie_name) {
      senderAssets.push({
        name: trade.sender_goalie_name,
        type: 'goalie',
        salary: trade.sender_goalie_salary
      });
    }
    if (trade.sender_pick_description) {
      senderAssets.push({
        name: trade.sender_pick_description,
        type: 'pick'
      });
    }

    // Ajouter les assets du receiver
    if (trade.receiver_player_name) {
      receiverAssets.push({
        name: trade.receiver_player_name,
        type: 'player',
        salary: trade.receiver_salary
      });
    }
    if (trade.receiver_goalie_name) {
      receiverAssets.push({
        name: trade.receiver_goalie_name,
        type: 'goalie',
        salary: trade.receiver_goalie_salary
      });
    }
    if (trade.receiver_pick_description) {
      receiverAssets.push({
        name: trade.receiver_pick_description,
        type: 'pick'
      });
    }
  });

  return { senderAssets, receiverAssets };
}

// Fonction pour obtenir les informations d'une ligue
export async function getLeagueInfo(leagueId: number): Promise<{ name: string; error?: string }> {
  try {
    const { data, error } = await supabase
      .from('leagues')
      .select('name')
      .eq('league_id', leagueId)
      .single();

    if (error) {
      console.error('Error fetching league info:', error);
      return { name: 'Ligue Inconnue', error: error.message };
    }

    return { name: data?.name || 'Ligue Inconnue' };
  } catch (error: any) {
    console.error('Error fetching league info:', error);
    return { name: 'Ligue Inconnue', error: error.message };
  }
}

// Fonction pour obtenir l'email d'un utilisateur
export async function getUserEmail(userId: number): Promise<{ email: string; username: string; error?: string }> {
  try {
    const { data, error } = await supabase
      .from('users')
      .select('email, username')
      .eq('user_id', userId)
      .single();

    if (error) {
      console.error('Error fetching user info:', error);
      return { email: '', username: '', error: error.message };
    }

    return { 
      email: data?.email || '', 
      username: data?.username || '',
    };
  } catch (error: any) {
    console.error('Error fetching user info:', error);
    return { email: '', username: '', error: error.message };
  }
}

// Fonction pour envoyer une notification de demande d'adhésion
export async function sendJoinRequestNotificationEmail(data: JoinRequestEmailData): Promise<{ success: boolean; error?: string }> {
  try {
    console.log('📧 [DEBUG] Starting join request notification email...');
    console.log('📧 [DEBUG] Email data:', JSON.stringify(data, null, 2));
    
    // Vérifier que toutes les données nécessaires sont présentes
    if (!data.commissionerEmail) {
      console.error('❌ [ERROR] Commissioner email is missing');
      return { success: false, error: 'Email du commissaire manquant' };
    }
    
    if (!data.requesterUsername) {
      console.error('❌ [ERROR] Requester username is missing');
      return { success: false, error: 'Nom d\'utilisateur du demandeur manquant' };
    }
    
    if (!data.leagueName) {
      console.error('❌ [ERROR] League name is missing');
      return { success: false, error: 'Nom de la ligue manquant' };
    }
    
    console.log('📧 [DEBUG] All required data present, calling resendEmailService...');
    const result = await resendEmailService.sendJoinRequestNotification(data);
    
    console.log('📧 [DEBUG] Resend service result:', JSON.stringify(result, null, 2));
    
    if (result.success) {
      console.log('✅ Join request notification email sent successfully via Resend from frontend');
    } else {
      console.error('❌ Failed to send join request notification email:', result.error);
    }
    
    return result;
  } catch (error: any) {
    console.error('❌ Error sending join request notification email from frontend:', error);
    console.error('❌ Error stack:', error.stack);
    return { success: false, error: error.message || 'Unknown error occurred' };
  }
}

// Fonction pour obtenir l'email du commissaire d'une ligue
export async function getCommissionerEmail(leagueId: number): Promise<{ email: string; username: string; error?: string }> {
  try {
    // D'abord, obtenons l'admin_user_id de la ligue
    const { data: leagueData, error: leagueError } = await supabase
      .from('leagues')
      .select('admin_user_id')
      .eq('league_id', leagueId)
      .single();

    if (leagueError) {
      console.error('Error fetching league admin_user_id:', leagueError);
      return { email: '', username: '', error: leagueError.message };
    }

    if (!leagueData?.admin_user_id) {
      return { email: '', username: '', error: 'No admin user found for this league' };
    }

    // Maintenant obtenons les infos du commissaire
    const { data: userData, error: userError } = await supabase
      .from('users')
      .select('email, username')
      .eq('user_id', leagueData.admin_user_id)
      .single();

    if (userError) {
      console.error('Error fetching commissioner info:', userError);
      return { email: '', username: '', error: userError.message };
    }

    return { 
      email: userData?.email || '', 
      username: userData?.username || '',
      error: undefined 
    };
  } catch (error: any) {
    console.error('Error fetching commissioner info:', error);
    return { email: '', username: '', error: error.message };
  }
}

// Notification au proposeur : son trade a été accepté ou refusé
export async function sendTradeResponseEmail(data: TradeResponseEmailData): Promise<{ success: boolean; error?: string }> {
  try {
    const result = await resendEmailService.sendTradeResponseNotification(data);
    if (!result.success) {
      console.warn('⚠️ Failed to send trade response email:', result.error);
    }
    return result;
  } catch (error: any) {
    console.error('❌ Error sending trade response email:', error);
    return { success: false, error: error.message || 'Unknown error occurred' };
  }
}

// Notification au demandeur : sa demande d'adhésion a été acceptée ou refusée
export async function sendJoinRequestDecisionEmail(data: JoinRequestDecisionEmailData): Promise<{ success: boolean; error?: string }> {
  try {
    const result = await resendEmailService.sendJoinRequestDecisionNotification(data);
    if (!result.success) {
      console.warn('⚠️ Failed to send join request decision email:', result.error);
    }
    return result;
  } catch (error: any) {
    console.error('❌ Error sending join request decision email:', error);
    return { success: false, error: error.message || 'Unknown error occurred' };
  }
}

// Notification au prochain gérant : c'est son tour au repêchage
export async function sendDraftTurnEmail(data: DraftTurnEmailData): Promise<{ success: boolean; error?: string }> {
  try {
    const result = await resendEmailService.sendDraftTurnNotification(data);
    if (!result.success) {
      console.warn('⚠️ Failed to send draft turn email:', result.error);
    }
    return result;
  } catch (error: any) {
    console.error('❌ Error sending draft turn email:', error);
    return { success: false, error: error.message || 'Unknown error occurred' };
  }
}

// Invitation d'un joueur externe à rejoindre une ligue par email
export async function sendLeagueInvitationEmail(data: LeagueInvitationEmailData): Promise<{ success: boolean; error?: string }> {
  try {
    const result = await resendEmailService.sendLeagueInvitationEmail(data);
    if (!result.success) {
      console.warn('⚠️ Failed to send league invitation email:', result.error);
    }
    return result;
  } catch (error: any) {
    console.error('❌ Error sending league invitation email:', error);
    return { success: false, error: error.message || 'Unknown error occurred' };
  }
}

// Confirmation aux 2 partis que le trade est officiel
export async function sendTradeFinalizedEmail(data: TradeFinalizedEmailData): Promise<{ success: boolean; error?: string }> {
  try {
    const result = await resendEmailService.sendTradeFinalizedNotification(data);
    if (!result.success) {
      console.warn('⚠️ Failed to send trade finalized email:', result.error);
    }
    return result;
  } catch (error: any) {
    console.error('❌ Error sending trade finalized email:', error);
    return { success: false, error: error.message || 'Unknown error occurred' };
  }
}

// Notification admin : nouvel utilisateur créé
export async function sendAdminNewUserEmail(data: AdminNewUserEmailData): Promise<{ success: boolean; error?: string }> {
  try {
    const result = await resendEmailService.sendAdminNewUserNotification(data);
    if (!result.success) {
      console.warn('⚠️ Failed to send admin new user email:', result.error);
    }
    return result;
  } catch (error: any) {
    console.error('❌ Error sending admin new user email:', error);
    return { success: false, error: error.message || 'Unknown error occurred' };
  }
}

// Notification admin : nouvelle ligue créée
export async function sendAdminNewLeagueEmail(data: AdminNewLeagueEmailData): Promise<{ success: boolean; error?: string }> {
  try {
    const result = await resendEmailService.sendAdminNewLeagueNotification(data);
    if (!result.success) {
      console.warn('⚠️ Failed to send admin new league email:', result.error);
    }
    return result;
  } catch (error: any) {
    console.error('❌ Error sending admin new league email:', error);
    return { success: false, error: error.message || 'Unknown error occurred' };
  }
}
